import { Button } from '@/components/ui/button';
import { useContentDetail } from '@/hooks/useContentQueries';
import { ExternalLink } from 'lucide-react';

export interface ProvenanceFileListProps {
  fileIds: number[];
  heading?: string;
  onOpenContent?: (contentId: number) => void;
}

function FileListItem({
  fileId,
  onOpenContent,
}: {
  fileId: number;
  onOpenContent?: (contentId: number) => void;
}) {
  const detailQuery = useContentDetail(fileId);
  const detail = detailQuery.data;
  const title =
    typeof detail?.title === 'string' && detail.title.trim().length > 0
      ? detail.title.trim()
      : `File ${fileId}`;
  const description =
    typeof detail?.description === 'string' && detail.description.trim().length > 0
      ? detail.description.trim()
      : null;

  return (
    <div className="rounded border p-2 space-y-1">
      <div className="flex items-center justify-between gap-2">
        <p className="font-medium text-xs">{title}</p>
        {onOpenContent ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label="Open file details"
            title="Open file details"
            onClick={() => onOpenContent(fileId)}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
        ) : null}
      </div>
      {description ? (
        <p className="text-xs text-muted-foreground">{description}</p>
      ) : (
        <p className="text-xs text-muted-foreground">Open file details to inspect the content.</p>
      )}
    </div>
  );
}

export function ProvenanceFileList({ fileIds, heading = 'Files', onOpenContent }: ProvenanceFileListProps) {
  if (fileIds.length === 0) {
    return null;
  }

  return (
    <div className="py-2 text-xs bg-muted/30 space-y-2">
      <p className="font-medium">{heading}</p>
      <div className="space-y-2">
        {fileIds.map((fileId) => (
          <FileListItem key={fileId} fileId={fileId} onOpenContent={onOpenContent} />
        ))}
      </div>
    </div>
  );
}
